import {
	LoadingBox,
	LoadingContent,
	LoadingList,
	LoadingPage,
	LoadingProgress,
	LoadingSpinner,
	LoadingText,
	RenderRequestError,
} from "@bokio/designsystem/components/Loading";

import type { RenderRequestErrorData } from "@bokio/designsystem/components/Loading/RenderRequestError";
import type { Envelope } from "@bokio/mobile-web-shared/core/model/model";
import type { MetaData } from "@bokio/mobile-web-shared/core/model/types";
import type { RequestState } from "@bokio/mobile-web-shared/services/api/requestState";
import type { ReactNode } from "react";

export type LoadingComponents = "progress" | "spinner" | "list" | "box" | "text" | "content" | "page";

// 2025-11-13 PontusH - LoadingComponents is overridden by the string in ReactNode, not sure how to fix without breaking the api.
// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
const mapLoadingComponent = (whenLoading: LoadingComponents | ReactNode | (() => ReactNode)): ReactNode | null => {
	if (typeof whenLoading === "function") {
		return whenLoading();
	}

	switch (whenLoading) {
		case "spinner":
			return <LoadingSpinner />;
		case "progress":
			return <LoadingProgress />;
		case "box":
			return <LoadingBox />;
		case "list":
			return <LoadingList />;
		case "text":
			return <LoadingText />;
		case "content":
			return <LoadingContent />;
		case "page":
			return <LoadingPage />;
		default:
			return whenLoading ?? null;
	}
};

interface RenderRequestProps<Result, TError> {
	request: RequestState<Envelope<Result, TError>>;
	// 2025-11-13 PontusH - LoadingComponents is overridden by the string in ReactNode, not sure how to fix without breaking the api.
	// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
	whenLoading: LoadingComponents | ReactNode | (() => ReactNode);
	whenError?: "useDefault" | ((result: RenderRequestErrorData<TError>) => ReactNode);
	noErrorNoticeTopMargin?: boolean;
	errorNoticeMarginBottom?: boolean;
	hideSoftErrors?: boolean;
	useCacheWhenLoading?: boolean;
	children?: (state: Result, meta?: MetaData) => ReactNode;
}

/** Renders a request and helps you with null-propagation.
 * @example
 * <RenderRequest
 * 	request={SomeRequestWithEnvelope}
 * 	whenLoading="progress"
 * 	whenError="useDefault"
 * >
 * 	{data => <Content data={data} />}
 * </RenderRequest>
 *
 */
export default function RenderRequest<Result, TError>({
	request,
	children,
	whenLoading,
	useCacheWhenLoading,
	noErrorNoticeTopMargin,
	errorNoticeMarginBottom,
	hideSoftErrors = false,
	whenError = "useDefault",
}: RenderRequestProps<Result, TError>) {
	const useCache = useCacheWhenLoading && request.data !== undefined;

	const showWhenLoaded =
		request.data && request.data.Success ? (children ? children(request.data.Data, request.data.Meta) : null) : null;

	if (useCache && showWhenLoaded) {
		return <>{showWhenLoaded}</>;
	}

	return (
		<RenderRequestError
			request={request}
			whenError={whenError}
			noNoticeTopMargin={noErrorNoticeTopMargin}
			noticeMarginBottom={errorNoticeMarginBottom}
			hideSoftErrors={hideSoftErrors}
			showWhenLoaded={showWhenLoaded}
		>
			{mapLoadingComponent(whenLoading)}
		</RenderRequestError>
	);
}
